
import {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";

interface ListsContextProps {
  whitelist: string[];
  blacklist: string[];
  setBlacklist: Dispatch<SetStateAction<string[]>>;
  setWhitelist: Dispatch<SetStateAction<string[]>>;
  /**
   * Recipient-selection mode for the normal-mode email send.
   * `false` (default) → explicit selection: the recipient set is the `whitelist`
   * only (empty = nobody). `true` → "all matching the active filter": the
   * filter-based set, with `whitelist`/`blacklist` augmenting/excluding it.
   * The send starts empty (`false`) and "Add all" flips it to `true`.
   */
  allRecipients: boolean;
  setAllRecipients: Dispatch<SetStateAction<boolean>>;
}

const ListsContext = createContext<ListsContextProps | undefined>(undefined);

interface ListsProviderProps {
  children: ReactNode;
}
export const ListsProvider = ({ children }: ListsProviderProps) => {
  const [blacklist, setBlacklist] = useState<string[]>([]);
  const [whitelist, setWhitelist] = useState<string[]>([]);
  const [allRecipients, setAllRecipients] = useState<boolean>(false);

  const contextValue = useMemo(() => ({
    whitelist, blacklist, setWhitelist, setBlacklist, allRecipients, setAllRecipients,
  }), [whitelist, blacklist, setWhitelist, setBlacklist, allRecipients, setAllRecipients]);

  return (
    <ListsContext.Provider value={contextValue}>
      {children}
    </ListsContext.Provider>
  );
};

export const useLists = () => {
  const context = useContext(ListsContext);
  return context;
};
